import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';

const NAV_ITEMS = [
  { to: '/',            label: 'Dashboard',    icon: '▦', exact: true },
  { to: '/employees',   label: 'Xodimlar',     icon: '👥' },
  { to: '/salaries',    label: 'Oyliklar',     icon: '💰' },
  { to: '/upload-logs', label: 'Yuklash Tarixi', icon: '📋' },
];

export default function Sidebar({ onCollapse }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);

  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    if (onCollapse) onCollapse(next);
  };

  const W = collapsed ? 72 : 232;

  return (
    <aside style={{
      width: W,
      minHeight: '100vh',
      background: '#0c4a6e',
      display: 'flex',
      flexDirection: 'column',
      position: 'fixed',
      left: 0, top: 0,
      zIndex: 100,
      transition: 'width 0.25s cubic-bezier(0.4,0,0.2,1)',
      overflow: 'hidden',
    }}>

      {/* ── Header ───────────────────────────────────────────── */}
      <div style={{
        height: 64,
        display: 'flex',
        alignItems: 'center',
        padding: '0 16px',
        borderBottom: '1px solid rgba(255,255,255,0.07)',
        flexShrink: 0,
        gap: 10,
        justifyContent: collapsed ? 'center' : 'space-between',
      }}>
        {!collapsed && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, overflow: 'hidden' }}>
            <div style={{
              width: 32, height: 32, flexShrink: 0,
              background: '#0ea5e9',
              borderRadius: 8,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 15,
            }}>💼</div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', letterSpacing: '-0.01em' }}>
                Oylik Bot
              </div>
              <div style={{ fontSize: 10, color: '#7dd3fc', letterSpacing: '0.06em' }}>
                VCHD-8
              </div>
            </div>
          </div>
        )}

        {/* Modern hamburger toggle */}
        <button
          onClick={toggle}
          title={collapsed ? 'Kengaytirish' : 'Yopish'}
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: '6px',
            borderRadius: 6,
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            flexShrink: 0,
            opacity: 0.7,
            transition: 'opacity 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.opacity = '1'}
          onMouseLeave={e => e.currentTarget.style.opacity = '0.7'}
        >
          <span style={{
            display: 'block',
            width: collapsed ? 16 : 18,
            height: 2,
            background: '#fff',
            borderRadius: 2,
            transition: 'width 0.2s',
          }} />
          <span style={{
            display: 'block',
            width: collapsed ? 10 : 18,
            height: 2,
            background: '#fff',
            borderRadius: 2,
            transition: 'width 0.2s',
          }} />
          <span style={{
            display: 'block',
            width: collapsed ? 14 : 18,
            height: 2,
            background: '#fff',
            borderRadius: 2,
            transition: 'width 0.2s',
          }} />
        </button>
      </div>

      {/* ── Navigation ──────────────────────────────────────── */}
      <nav style={{ flex: 1, padding: '10px 10px' }}>
        {!collapsed && (
          <div style={{
            fontSize: 10, fontWeight: 700,
            color: '#38bdf8', letterSpacing: '0.1em',
            padding: '6px 8px 4px',
            textTransform: 'uppercase',
          }}>
            Menyu
          </div>
        )}

        {NAV_ITEMS.map(({ to, label, icon, exact }) => {
          const active = exact
            ? location.pathname === to
            : location.pathname.startsWith(to);

          return (
            <NavLink
              key={to}
              to={to}
              title={collapsed ? label : undefined}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: collapsed ? 'center' : 'flex-start',
                gap: 10,
                padding: collapsed ? '11px 0' : '10px 12px',
                borderRadius: 8,
                marginBottom: 2,
                color: active ? '#fff' : 'rgba(255,255,255,0.6)',
                background: active ? 'rgba(14,165,233,0.25)' : 'transparent',
                fontWeight: active ? 600 : 400,
                fontSize: 13.5,
                textDecoration: 'none',
                transition: 'background 0.15s, color 0.15s',
                position: 'relative',
                whiteSpace: 'nowrap',
                borderLeft: active && !collapsed ? '2px solid #38bdf8' : '2px solid transparent',
              }}
              onMouseEnter={e => { if (!active) { e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; e.currentTarget.style.color = '#fff'; }}}
              onMouseLeave={e => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; }}}
            >
              <span style={{ fontSize: 15, flexShrink: 0, lineHeight: 1 }}>{icon}</span>
              {!collapsed && label}
            </NavLink>
          );
        })}
      </nav>

      {/* ── User + logout ────────────────────────────────────── */}
      <div style={{
        padding: '10px',
        borderTop: '1px solid rgba(255,255,255,0.07)',
      }}>
        {!collapsed && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '8px 10px', borderRadius: 8,
            background: 'rgba(255,255,255,0.05)',
            marginBottom: 8,
          }}>
            <div style={{
              width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
              background: '#0ea5e9',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 12, fontWeight: 700, color: '#fff',
            }}>
              {(user?.username?.[0] || 'A').toUpperCase()}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {user?.username || 'Admin'}
              </div>
              <div style={{ fontSize: 10, color: '#7dd3fc' }}>Administrator</div>
            </div>
          </div>
        )}

        <button
          onClick={logout}
          title={collapsed ? 'Chiqish' : undefined}
          style={{
            width: '100%',
            padding: '8px 10px',
            background: 'transparent',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 8,
            color: 'rgba(255,255,255,0.55)',
            fontSize: 12,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            transition: 'border-color 0.15s, color 0.15s, background 0.15s',
          }}
          onMouseEnter={e => {
            e.currentTarget.style.borderColor = 'rgba(244,63,94,0.5)';
            e.currentTarget.style.color = '#fda4af';
            e.currentTarget.style.background = 'rgba(244,63,94,0.08)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
            e.currentTarget.style.color = 'rgba(255,255,255,0.55)';
            e.currentTarget.style.background = 'transparent';
          }}
        >
          <span style={{ fontSize: 13 }}>🚪</span>
          {!collapsed && 'Chiqish'}
        </button>
      </div>
    </aside>
  );
}
